import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { botRunService } from "../services/botRunService";
import { botRunDisplayId } from "./BotRunCommonStore";
import { BotRun } from "../../../api/graphql";
import { workerEventDisplayId } from "../../workerEvent/store/WorkerEventCommonStore";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { getBotPhaseValues } from "../../botPhase/store/BotPhaseCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { getBotErrorValues } from "../../botError/store/BotErrorCommonStore";
import { CrudRows } from "../../common/types";
import { BotType } from "../../../api/graphql";
import { workerEventService } from "../../workerEvent/services/workerEventService";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class BotRunViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = botRunService;
  botRunInstance: BotRun | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  botTypeValue: BotType = "" as BotType;
  runNumberValue: number = 0;
  statusValue: string = "";
  currentPhaseValue: string = "";
  startedAtValue: Date | null = null;
  completedAtValue: Date | null = null;
  durationValue: number | undefined = undefined;
  paramsValue: string = "{}";
  resultValue: string = "{}";
  errorValue: string = "{}";
  correlationIdValue: string = "";
  totalPhasesValue: number = 0;
  completedPhasesValue: number = 0;
  requiresHumanInterventionValue: boolean = false;
  interventionReasonValue: string = "";
  logsPathValue: string = "";
  workerEventService = workerEventService;
  workerEventSearchValue: string = "";
  workerEventIdValue: string = "";
  workerEventSelectedValue: { displayId: string; id: string } | null = null;
  triggeredByService = userService;
  triggeredBySearchValue: string = "";
  triggeredByIdValue: string = "";
  triggeredBySelectedValue: { displayId: string; id: string } | null = null;
  relatedEntityTypeValue: string = "";
  relatedEntityIdValue: string = "";
  relatedEntityLabelValue: string = "";
  cancellationRequestedAtValue: Date | null = null;
  cancellationRequestedByService = userService;
  cancellationRequestedBySearchValue: string = "";
  cancellationRequestedByIdValue: string = "";
  cancellationRequestedBySelectedValue: {
    displayId: string;
    id: string;
  } | null = null;
  cancellationReasonValue: string = "";
  phasesColumnWidths: Record<string, number | string> = {};
  phasesRows: CrudRows = {};
  errorsColumnWidths: Record<string, number | string> = {};
  errorsRows: CrudRows = {};

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.phasesColumnWidths = {
      phaseNumber: 200,
      phaseName: 200,
      status: 200,
      duration: 200,
    };
    this.errorsColumnWidths = { errorType: 200, severity: 200, message: 200 };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadBotRunById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.botRunOne({ id });
    this.botRunInstance = result.botRunOne;
    if (this.botRunInstance) {
      this.loadValues(this.botRunInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.botRunInstance) {
      return botRunDisplayId(this.botRunInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.BotRunSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "workerEvent") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.workerEventOne({
          id: params.returnData.resultId,
        });
        const workerEvent = result.workerEventOne;
        this.workerEventSelectedValue = workerEvent;
      } else if (params.callerData?.fieldName === "triggeredBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.triggeredBySelectedValue = user;
      } else if (params.callerData?.fieldName === "cancellationRequestedBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.cancellationRequestedBySelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getBotTypeOptions() {
    return this.langStore.BotTypeOptions;
  }

  get workerEventValue() {
    if (this.workerEventSelectedValue) {
      return {
        id: this.workerEventSelectedValue.id,
        displayId: workerEventDisplayId(
          this.workerEventSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get triggeredByValue() {
    if (this.triggeredBySelectedValue) {
      return {
        id: this.triggeredBySelectedValue.id,
        displayId: userDisplayId(this.triggeredBySelectedValue, this.langStore),
      };
    }
    return null;
  }

  get cancellationRequestedByValue() {
    if (this.cancellationRequestedBySelectedValue) {
      return {
        id: this.cancellationRequestedBySelectedValue.id,
        displayId: userDisplayId(
          this.cancellationRequestedBySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get phasesVisibleColumns() {
    return [
      {
        name: "phaseNumber",
        title: this.langStore.BotPhasePhaseNumberLabel,
        width: this.phasesColumnWidths["phaseNumber"],
      },
      {
        name: "phaseName",
        title: this.langStore.BotPhasePhaseNameLabel,
        width: this.phasesColumnWidths["phaseName"],
      },
      {
        name: "status",
        title: this.langStore.BotPhaseStatusLabel,
        width: this.phasesColumnWidths["status"],
      },
      {
        name: "duration",
        title: this.langStore.BotPhaseDurationLabel,
        width: this.phasesColumnWidths["duration"],
      },
    ];
  }

  phasesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.phasesColumnWidths = values;
  }

  get phasesRowsDyn() {
    return this.phasesRows;
  }

  get errorsVisibleColumns() {
    return [
      {
        name: "errorType",
        title: this.langStore.BotErrorErrorTypeLabel,
        width: this.errorsColumnWidths["errorType"],
      },
      {
        name: "severity",
        title: this.langStore.BotErrorSeverityLabel,
        width: this.errorsColumnWidths["severity"],
      },
      {
        name: "message",
        title: this.langStore.BotErrorMessageLabel,
        width: this.errorsColumnWidths["message"],
      },
    ];
  }

  errorsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.errorsColumnWidths = values;
  }

  get errorsRowsDyn() {
    return this.errorsRows;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.botRunInstance = null;
    this.botTypeValue = "" as BotType;
    this.runNumberValue = 0;
    this.statusValue = "";
    this.currentPhaseValue = "";
    this.startedAtValue = null;
    this.completedAtValue = null;
    this.durationValue = undefined;
    this.paramsValue = "{}";
    this.resultValue = "{}";
    this.errorValue = "{}";
    this.correlationIdValue = "";
    this.totalPhasesValue = 0;
    this.completedPhasesValue = 0;
    this.requiresHumanInterventionValue = false;
    this.interventionReasonValue = "";
    this.logsPathValue = "";
    this.workerEventSearchValue = "";
    this.workerEventIdValue = "";
    this.triggeredBySearchValue = "";
    this.triggeredByIdValue = "";
    this.relatedEntityTypeValue = "";
    this.relatedEntityIdValue = "";
    this.relatedEntityLabelValue = "";
    this.cancellationRequestedAtValue = null;
    this.cancellationRequestedBySearchValue = "";
    this.cancellationRequestedByIdValue = "";
    this.cancellationReasonValue = "";
    this.phasesRows = {};
    this.errorsRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelBotRun() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editBotRun() {
    const id = this.botRunInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/botRun/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printBotRun(handlePrint: () => void): void {
    handlePrint();
  }

  *closeBotRun(): any {
    yield this.close();
  }

  loadValues(instance: BotRun) {
    this.idValue = instance.id;
    this.botTypeValue = instance.botType;
    this.runNumberValue = instance.runNumber;
    this.statusValue = instance.status;
    this.currentPhaseValue = instance.currentPhase || "";
    this.startedAtValue = instance.startedAt
      ? new Date(instance.startedAt)
      : null;
    this.completedAtValue = instance.completedAt
      ? new Date(instance.completedAt)
      : null;
    this.durationValue = instance.duration || undefined;
    this.paramsValue = instance.params;
    this.resultValue = instance.result || "";
    this.errorValue = instance.error || "";
    this.correlationIdValue = instance.correlationId;
    this.totalPhasesValue = instance.totalPhases;
    this.completedPhasesValue = instance.completedPhases;
    this.requiresHumanInterventionValue = instance.requiresHumanIntervention;
    this.interventionReasonValue = instance.interventionReason || "";
    this.logsPathValue = instance.logsPath || "";
    this.workerEventSelectedValue = instance.workerEvent
      ? instance.workerEvent
      : null;
    this.triggeredBySelectedValue = instance.triggeredBy;
    this.relatedEntityTypeValue = instance.relatedEntityType || "";
    this.relatedEntityIdValue = instance.relatedEntityId || "";
    this.relatedEntityLabelValue = instance.relatedEntityLabel || "";
    this.cancellationRequestedAtValue = instance.cancellationRequestedAt
      ? new Date(instance.cancellationRequestedAt)
      : null;
    this.cancellationRequestedBySelectedValue = instance.cancellationRequestedBy
      ? instance.cancellationRequestedBy
      : null;
    this.cancellationReasonValue = instance.cancellationReason || "";
    this.phasesRows = instance.phases
      ? Object.assign(
          {},
          ...instance.phases.map((x) => {
            const processedRow = getBotPhaseValues(x, this.langStore, false);
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.errorsRows = instance.errors
      ? Object.assign(
          {},
          ...instance.errors.map((x) => {
            const processedRow = getBotErrorValues(x, this.langStore, false);
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadBotRunById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
